// Claim as a first-class object: verdict ribbon, confidence badge, source
// chips, as-of staleness, skeptic justification. "Not graded" is a rendered
// state, not a hidden failure.

import { useState } from "react";
import type {
  ParsedClaim,
  ParsedSource,
  ParsedVerdict,
} from "@ideacrunch/shared";
import { ConfidenceBadge, StaleBadge, VerdictChip } from "./bits";

export function ClaimCard({
  claim,
  verdict,
  claimRef,
  sources,
}: {
  claim: ParsedClaim;
  verdict: ParsedVerdict | null;
  claimRef: string | null;
  sources: ParsedSource[];
}) {
  const [showJustification, setShowJustification] = useState(false);

  const vClass = claim.isAssumption
    ? "assumption"
    : verdict?.verdictNorm === "SUPPORTED"
      ? "v-supported"
      : verdict?.verdictNorm === "WEAK"
        ? "v-weak"
        : verdict?.verdictNorm === "REFUTED"
          ? "v-refuted"
          : "v-unknown";

  const claimSources = claim.sourceTags
    .map((t) => sources.find((s) => s.tag === t))
    .filter((s): s is ParsedSource => s !== undefined);

  return (
    <div className={`claim-card ${vClass}`}>
      <div className="head">
        {claim.isAssumption ? (
          <span className="chip warn">ASSUMPTION</span>
        ) : verdict ? (
          <VerdictChip norm={verdict.verdictNorm} raw={verdict.verdictRaw} />
        ) : (
          <span className="chip ghost">not graded</span>
        )}
        {claimRef && <span className="chip ghost">{claimRef}</span>}
        {claim.inRevision !== null && (
          <span className="chip accent">revision {claim.inRevision}</span>
        )}
      </div>
      <div className="text">{claim.text}</div>
      <div className="meta-line">
        {claimSources.map((s) =>
          s.url ? (
            <a
              key={s.tag}
              className="chip accent"
              href={s.url}
              target="_blank"
              rel="noreferrer"
              title={`${s.title}${s.date ? ` (${s.date})` : ""}${s.note ? ` - ${s.note}` : ""}`}
            >
              {s.tag}
            </a>
          ) : (
            <span key={s.tag} className="chip ghost" title={s.title}>
              {s.tag}
            </span>
          ),
        )}
        <ConfidenceBadge
          confidence={claim.confidence}
          modelKnowledge={claim.isModelKnowledge}
        />
        <StaleBadge asOf={claim.asOf} />
        {claim.isAssumption && claim.assumptionTest && (
          <span className="chip ghost" title={claim.assumptionTest}>
            test: {claim.assumptionTest.slice(0, 60)}
            {claim.assumptionTest.length > 60 ? "..." : ""}
          </span>
        )}
        {verdict && (
          <button
            className="chip ghost clickable"
            onClick={() => setShowJustification((v) => !v)}
            style={{ border: "1px solid var(--hairline)" }}
          >
            {showJustification ? "hide" : "what the Skeptic checked"}
          </button>
        )}
      </div>
      {showJustification && verdict && (
        <div className="skeptic-note">{verdict.justification}</div>
      )}
    </div>
  );
}
